import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, createRoutesFromElements } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { auth } from './config/firebase';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { usePointsStore } from './store/pointsStore';
import { subscribeToSystemNotifications } from './services/notificationService';
import { getUserPoints } from './services/userService';
import { ErrorBoundaryProvider } from './components/ErrorBoundary';
import { cleanupAllStorage } from './services/storage/storageCleanupService';
import { restoreAuthSession } from './services/auth/persistenceService';
import MainLayout from './components/Layout/MainLayout';
import LoadingScreen from './components/LoadingScreen';
import AuthGuard from './components/AuthGuard';
import LicenseGuard from './components/LicenseGuard';
import AdminRoute from './components/AdminRoute';
import { TicketButton } from './components/SupportTicket';
import InteractiveGuide from './components/Guide/InteractiveGuide';
import AnnouncementBanner from './components/AnnouncementBanner';

// Lazy loaded pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminLicensesList = React.lazy(() => import('./pages/AdminLicensesList'));
const AdminUsersList = React.lazy(() => import('./pages/AdminUsersList'));
const AdminLicenseCreation = React.lazy(() => import('./pages/AdminLicenseCreation'));
const AdminLicenseActivate = React.lazy(() => import('./pages/AdminLicenseActivate'));
const AdminLicenseDeactivate = React.lazy(() => import('./pages/AdminLicenseDeactivate'));
const AdminLicensePause = React.lazy(() => import('./pages/AdminLicensePause'));
const AdminLicenseEnable = React.lazy(() => import('./pages/AdminLicenseEnable'));
const AdminLicenseRemoval = React.lazy(() => import('./pages/AdminLicenseRemoval'));
const Activate = React.lazy(() => import('./pages/Activate'));
const Deactivate = React.lazy(() => import('./pages/Deactivate'));
const Update = React.lazy(() => import('./pages/Update'));
const Download = React.lazy(() => import('./pages/Download'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const Services = React.lazy(() => import('./pages/Services'));
const SvgColorEditor = React.lazy(() => import('./pages/SvgColorEditor'));
const ImageOptimizer = React.lazy(() => import('./pages/ImageOptimizer'));
const ImageResizer = React.lazy(() => import('./pages/ImageResizer'));
const WinningProducts = React.lazy(() => import('./pages/WinningProducts'));
const Changelog = React.lazy(() => import('./pages/Changelog'));
const DocumentationDev = React.lazy(() => import('./pages/DocumentationDev'));
const Help = React.lazy(() => import('./pages/Help'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const PaymentSuccess = React.lazy(() => import('./pages/payment/Success'));
const PaymentCancel = React.lazy(() => import('./pages/payment/Cancel'));
const ThemeInstallerDemo = React.lazy(() => import('./pages/ThemeInstallerDemo'));
const PageTemplates = React.lazy(() => import('./pages/PageTemplates'));
const ProductTemplates = React.lazy(() => import('./pages/ProductTemplates'));
const CollectionTemplates = React.lazy(() => import('./pages/CollectionTemplates'));
const BlogTemplates = React.lazy(() => import('./pages/BlogTemplates'));
const TemplatesHub = React.lazy(() => import('./pages/TemplatesHub'));

const App: React.FC = () => {
  const { isDark } = useThemeStore();
  const { user, isAdmin } = useAuthStore();

  useEffect(() => {
    const initAuth = async () => {
      try {
        await restoreAuthSession();
      } catch (error) {
        console.error('Error initializing auth:', error);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = subscribeToSystemNotifications(user.uid, isAdmin);
      return () => unsubscribe();
    }
  }, [user?.uid, isAdmin]);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  return (
    <ErrorBoundaryProvider>
      <BrowserRouter future={{ v7_relativeSplatPath: true }}>
        {/* Announcement Banner inside BrowserRouter to have access to routing */}
        <AnnouncementBanner />
        
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            {/* Protected routes */}
            <Route element={<AuthGuard />}>
              {/* Main routes with layout */}
              <Route path="/" element={renderWithLayout(<Dashboard />)} />
              <Route path="/profile" element={renderWithLayout(<Profile />)} />
              <Route path="/activate" element={
                <LicenseGuard>
                  {renderWithLayout(<Activate />)}
                </LicenseGuard>
              } />
              <Route path="/theme-installer" element={
                <LicenseGuard>
                  {renderWithLayout(<ThemeInstallerDemo />)}
                </LicenseGuard>
              } />
              <Route path="/update" element={
                <LicenseGuard>
                  {renderWithLayout(<Update />)}
                </LicenseGuard>
              } />
              <Route path="/download" element={
                <LicenseGuard>
                  {renderWithLayout(<Download />)}
                </LicenseGuard>
              } />
              <Route path="/deactivate" element={
                <LicenseGuard>
                  {renderWithLayout(<Deactivate />)}
                </LicenseGuard>
              } />
              <Route path="/changelog" element={renderWithLayout(<Changelog />)} />
              <Route path="/help" element={renderWithLayout(<Help />)} />
              <Route path="/documentation-dev" element={renderWithLayout(<DocumentationDev />)} />
              <Route path="/svg-color" element={renderWithLayout(<SvgColorEditor />)} />
              <Route path="/image-optimizer" element={renderWithLayout(<ImageOptimizer />)} />
              <Route path="/image-resizer" element={renderWithLayout(<ImageResizer />)} />
              <Route path="/services" element={
                <LicenseGuard>
                  {renderWithLayout(<Services />)}
                </LicenseGuard>
              } />
              <Route path="/winning-products" element={renderWithLayout(<WinningProducts />)} />

              {/* Admin routes */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={renderWithLayout(<AdminDashboard />)} />
                <Route path="/admin/licenses" element={renderWithLayout(<AdminLicensesList />)} />
                <Route path="/admin/users" element={renderWithLayout(<AdminUsersList />)} />
                <Route path="/admin/create-license" element={renderWithLayout(<AdminLicenseCreation />)} />
                <Route path="/admin/activate-license" element={renderWithLayout(<AdminLicenseActivate />)} />
                <Route path="/admin/deactivate-license" element={renderWithLayout(<AdminLicenseDeactivate />)} />
                <Route path="/admin/pause-license" element={renderWithLayout(<AdminLicensePause />)} />
                <Route path="/admin/enable-license" element={renderWithLayout(<AdminLicenseEnable />)} />
                <Route path="/admin/remove-license" element={renderWithLayout(<AdminLicenseRemoval />)} />
                <Route path="/templates" element={renderWithLayout(<TemplatesHub />)} />
                <Route path="/page-templates" element={renderWithLayout(<PageTemplates />)} />
                <Route path="/product-templates" element={renderWithLayout(<ProductTemplates />)} />
                <Route path="/collection-templates" element={renderWithLayout(<CollectionTemplates />)} />
                <Route path="/blog-templates" element={renderWithLayout(<BlogTemplates />)} />
              </Route>
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Support Ticket Button */}
        {user && <TicketButton />}

        {/* Interactive Guide */}
        {user && <InteractiveGuide />}

        <Toaster 
          position="top-right" 
          toastOptions={{
            className: 'dark:bg-gray-800 dark:text-white',
            style: {
              background: isDark ? '#1f2937' : '#fff',
              color: isDark ? '#fff' : '#000',
            },
          }}
        />
      </BrowserRouter>
    </ErrorBoundaryProvider>
  );
};

const renderWithLayout = (element: React.ReactNode) => (
  <MainLayout>{element}</MainLayout>
);

export default App;